import React, { FC } from 'react';
import Lozenge from '@atlaskit/lozenge';
import Tooltip from '@atlaskit/tooltip';
import { css } from '@emotion/core';
import { LowTrafficStatusDTO, SloInstanceDto, SloType } from '@atlassiansox/phobos-client-ts';
import GraphLineIcon from '@atlaskit/icon/glyph/graph-line';
import isNil from 'lodash/isNil';

import {
  flexCenter,
  LozengeExtendedContainerStyles,
  SPACE_COZY,
  spaceGapMinimum,
  textTransformLowercase,
} from '@/styles';
import SfxIcon from '@/components/primitive/sfx-icon';
import SloMenu from '@/components/primitive/slo-menu';
import { useLocationController } from '@/controllers';

import { SLINumberProps } from '../common/utils';
import SloTypeLozenge from '../../slo-card/lozenges/slo-type-lozenge';
import { getDynamicCohortMemberTagName, getMimirCohortMemberTagName } from '../../slo-group/utils';
import { SLOIndicatorWithGroupType } from '../../slo-group/types';

import { SLOLifecycleStatus } from './types';

const rootStyles = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const innerLeftStyles = css`
  display: flex;
  align-items: center;
  gap: ${SPACE_COZY}px;
`;

const headerGroupMemberTagStyles = css`
  ${textTransformLowercase};
  font-size: 12px;
`;

type SloPageHeaderProps = {
  publicId: string;
  name: string;
  type: SloType;
  indicator?: SLOIndicatorWithGroupType;
  sloLifecycleStatus: SLOLifecycleStatus;
  isAlertable?: boolean;
  isSkeleton?: boolean;
  parentCapabilityPublicId: string;
  lowTrafficStatus?: LowTrafficStatusDTO;
  isMimir: boolean;
  sloInstance?: SloInstanceDto;
  cohortTypeSorted?: string[];
} & SLINumberProps;

const SloPageHeader: FC<SloPageHeaderProps> = ({
  publicId,
  name,
  type,
  indicator,
  sloLifecycleStatus,
  isAlertable,
  isSkeleton,
  parentCapabilityPublicId,
  lowTrafficStatus,
  isMimir,
  sloInstance,
  cohortTypeSorted,
}) => {
  const { isSLOGroupPage, isSLOGroupMemberPage } = useLocationController();
  const isEditable =
    sloLifecycleStatus === SLOLifecycleStatus.DRAFT ||
    sloLifecycleStatus === SLOLifecycleStatus.COMMITTED;

  return (
    <div css={[rootStyles]} data-testid="slo-page-header">
      <div css={[innerLeftStyles]}>
        {isSLOGroupPage && <Lozenge appearance="inprogress">slo group</Lozenge>}
        {isSLOGroupMemberPage && (
          <span css={LozengeExtendedContainerStyles}>
            <Lozenge appearance="inprogress">
              <span css={headerGroupMemberTagStyles}>
                {isMimir
                  ? getMimirCohortMemberTagName(sloInstance)
                  : getDynamicCohortMemberTagName(indicator, cohortTypeSorted)}
              </span>
            </Lozenge>
          </span>
        )}
        {sloLifecycleStatus === SLOLifecycleStatus.DRAFT && (
          <Lozenge appearance="moved">🛠 Draft</Lozenge>
        )}
        {sloLifecycleStatus === SLOLifecycleStatus.SNAPSHOT && (
          <Lozenge appearance="removed">🛠 Deleted</Lozenge>
        )}

        <SloTypeLozenge type={type} isMimir={isMimir} />

        {!isSLOGroupPage &&
          lowTrafficStatus &&
          lowTrafficStatus.inLowTrafficState &&
          !isNil(lowTrafficStatus.currentTraffic) &&
          lowTrafficStatus.currentTraffic > 0 && (
            <Lozenge appearance="new">
              <div css={[flexCenter, spaceGapMinimum]}>
                <GraphLineIcon label="Low traffic" size="small" />
                low traffic
              </div>
            </Lozenge>
          )}
      </div>
      <div
        css={[
          flexCenter,
          css`
            gap: 1em;
          `,
        ]}
      >
        {sloLifecycleStatus === SLOLifecycleStatus.COMMITTED && isAlertable && (
          <Tooltip
            content="This SLO will raise alerts via SignalFx.
            When this SLO experiences a breach or burn event,
            it will page the owning team via OpsGenie"
            position="left"
          >
            <SfxIcon />
          </Tooltip>
        )}
        {isEditable && (
          <SloMenu
            sloLifecycleStatus={sloLifecycleStatus}
            publicId={publicId}
            isSkeleton={isSkeleton}
            name={name}
            indicator={indicator}
            parentCapabilityPublicId={parentCapabilityPublicId}
          />
        )}
      </div>
    </div>
  );
};

export default SloPageHeader;
